// ============================================================================
// caixaComandaStorage — CRUD de caixas, comandas e movimentações (Supabase)
// ============================================================================
// Substitui getCaixas/getComandas/getCashMovements + upsert/delete do v2
// que usavam localStorage. REST direto Supabase.
// ============================================================================

import { SUPABASE_URL, restHeaders, restFetchAll } from './secureRest.js';

const num = (v) => Number(v) || 0;
// UUID v4-ish — converte strings inválidas (vazio, "cli-...", "apt-...") em null
// pra evitar 22P02 "invalid input syntax for type uuid"
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUuidOrNull = (v) => (typeof v === 'string' && UUID_RE.test(v.trim()) ? v.trim() : null);

// ═══════════════════════════════════════════════════════════════════════════
// CAIXAS
// ═══════════════════════════════════════════════════════════════════════════

function caixaToApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    unitId: row.unit_id || '',
    date: row.date || '',
    openedAt: row.opened_at,
    openedBy: row.opened_by || '',
    openingBalance: num(row.opening_balance),
    closedAt: row.closed_at || null,
    closedBy: row.closed_by || '',
    closingBalance: row.closing_balance != null ? num(row.closing_balance) : null,
    expectedBalance: row.expected_balance != null ? num(row.expected_balance) : null,
    difference: row.difference != null ? num(row.difference) : null,
    status: row.status || 'aberto',
    notes: row.notes || '',
    audit: row.audit || [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function caixaToDb(c) {
  const r = {};
  if (c.unitId !== undefined) r.unit_id = c.unitId;
  if (c.date !== undefined) r.date = c.date;
  if (c.openedBy !== undefined) r.opened_by = c.openedBy || null;
  if (c.openingBalance !== undefined) r.opening_balance = num(c.openingBalance);
  if (c.closedAt !== undefined) r.closed_at = c.closedAt || null;
  if (c.closedBy !== undefined) r.closed_by = c.closedBy || null;
  if (c.closingBalance !== undefined) r.closing_balance = c.closingBalance == null ? null : num(c.closingBalance);
  if (c.expectedBalance !== undefined) r.expected_balance = c.expectedBalance == null ? null : num(c.expectedBalance);
  if (c.difference !== undefined) r.difference = c.difference == null ? null : num(c.difference);
  if (c.status !== undefined) r.status = c.status || 'aberto';
  if (c.notes !== undefined) r.notes = c.notes || null;
  if (c.audit !== undefined) r.audit = c.audit || [];
  return r;
}

// REGRA LINEAR (15/07/2026): leitura NUNCA devolve vazio em falha — lança.
// Quem chama (DataContext) mantém o estado anterior e avisa o usuário.
// E pagina até o fim: o PostgREST corta em 1000 linhas SILENCIOSAMENTE.
export async function listCaixas({ unitId, startDate, endDate, status } = {}) {
  const parts = ['select=*', 'order=date.desc,opened_at.desc'];
  if (unitId) parts.push(`unit_id=eq.${encodeURIComponent(unitId)}`);
  if (startDate) parts.push(`date=gte.${startDate}`);
  if (endDate) parts.push(`date=lte.${endDate}`);
  if (status) parts.push(`status=eq.${encodeURIComponent(status)}`);
  const rows = await restFetchAll(`${SUPABASE_URL}/rest/v1/caixas?${parts.join('&')}`);
  return rows.map(caixaToApp);
}

// Exclui de vez. Comandas: ON DELETE SET NULL (ficam sem o caixa_id).
// cash_movements: ON DELETE CASCADE — as sangrias/suprimentos do caixa são
// apagadas junto (migrations/caixas-comandas.sql). Por isso o /api/db só
// deixa o operador financeiro excluir caixa (06/10/2026).
export async function deleteCaixa(caixaId) {
  if (!caixaId) return false;
  const r = await fetch(`${SUPABASE_URL}/rest/v1/caixas?id=eq.${caixaId}`, {
    method: 'DELETE', headers: restHeaders(),
  });
  if (!r.ok) throw new Error(`Falha ao excluir caixa: ${await r.text()}`);
  return true;
}

export class CaixaAbertoError extends Error {
  constructor(existing) { super('Já existe um caixa aberto nesta unidade. Feche o caixa atual antes de abrir outro.'); this.name = 'CaixaAbertoError'; this.existing = existing; }
}

export async function upsertCaixa(caixa) {
  const row = { ...caixaToDb(caixa), updated_at: new Date().toISOString() };
  const isUpdate = caixa.id && caixa.id.length === 36 && caixa.id.includes('-');
  // G-01: um caixa ABERTO por unidade. Ao ABRIR (create com status aberto),
  // recusa se já houver um aberto. (A trava definitiva é o índice único parcial
  // do banco — ver migrations/stress-fixes.sql; isto dá o aviso amigável.)
  if (!isUpdate && (caixa.status || 'aberto') === 'aberto' && caixa.unitId) {
    const chk = await fetch(`${SUPABASE_URL}/rest/v1/caixas?unit_id=eq.${encodeURIComponent(caixa.unitId)}&status=eq.aberto&select=id,date&limit=1`, { headers: restHeaders() });
    if (chk.ok) {
      const open = await chk.json();
      if (open.length) throw new CaixaAbertoError(open[0]);
    }
  }
  if (isUpdate) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/caixas?id=eq.${caixa.id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao atualizar caixa: ${await r.text()}`);
    return caixaToApp((await r.json())[0]);
  }
  const r = await fetch(`${SUPABASE_URL}/rest/v1/caixas`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(row),
  });
  if (!r.ok) throw new Error(`Falha ao criar caixa: ${await r.text()}`);
  return caixaToApp((await r.json())[0]);
}

// ═══════════════════════════════════════════════════════════════════════════
// COMANDAS
// ═══════════════════════════════════════════════════════════════════════════

// payment_method foi salvo como text JSON em comandas antigas (string tipo
// '[{...}]'). Hoje deveria ir como JSONB direto. Esse helper aceita ambos:
// se for string JSON parseável, parseia; senão retorna como veio.
function parseJSONIfString(v) {
  if (typeof v !== 'string') return v;
  if (!v.startsWith('[') && !v.startsWith('{')) return v; // string simples (ex: 'cortesia')
  try { return JSON.parse(v); } catch { return v; }
}

function comandaToApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    number: row.number || null,
    date: row.date || '',
    unitId: row.unit_id || '',
    caixaId: row.caixa_id || null,
    clientId: row.client_id || '',
    clientName: row.client_name || '',
    clientPhone: row.client_phone || '',
    professionalId: row.professional_id || '',
    professionalName: row.professional_name || '',
    items: row.items || [],
    payments: row.payments || [],
    // JSONB normal → array; antes de migrar pra JSONB foi salvo como string
    // JSON. Aceita ambos pra getPaymentBreakdown poder fazer Array.isArray().
    paymentMethod: parseJSONIfString(row.payment_method) || '',
    subtotal: num(row.subtotal),
    discount: num(row.discount),
    tips: row.tips || [],
    total: num(row.total),
    appointmentId: row.appointment_id || null,
    status: row.status || 'aberta',
    closedAt: row.closed_at || null,
    closedBy: row.closed_by || '',
    reopenedAt: row.reopened_at || null,
    reopenedBy: row.reopened_by || '',
    modifiedAt: row.modified_at || null,
    modifiedBy: row.modified_by || '',
    audit: row.audit || [],
    notes: row.notes || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function comandaToDb(c) {
  const r = {};
  if (c.number !== undefined) r.number = c.number == null ? null : Number(c.number);
  if (c.date !== undefined) r.date = c.date;
  if (c.unitId !== undefined) r.unit_id = c.unitId;
  if (c.caixaId !== undefined) r.caixa_id = asUuidOrNull(c.caixaId);
  if (c.clientId !== undefined) r.client_id = asUuidOrNull(c.clientId);
  if (c.clientName !== undefined) r.client_name = c.clientName || null;
  if (c.clientPhone !== undefined) r.client_phone = c.clientPhone || null;
  if (c.professionalId !== undefined) r.professional_id = c.professionalId || null;
  if (c.professionalName !== undefined) r.professional_name = c.professionalName || null;
  if (c.items !== undefined) r.items = c.items || [];
  if (c.payments !== undefined) r.payments = c.payments || [];
  if (c.paymentMethod !== undefined) r.payment_method = c.paymentMethod || null;
  if (c.subtotal !== undefined) r.subtotal = num(c.subtotal);
  if (c.discount !== undefined) r.discount = num(c.discount);
  if (c.tips !== undefined) r.tips = c.tips || [];
  if (c.total !== undefined) r.total = num(c.total);
  if (c.appointmentId !== undefined) r.appointment_id = asUuidOrNull(c.appointmentId);
  if (c.status !== undefined) r.status = c.status || 'aberta';
  if (c.closedAt !== undefined) r.closed_at = c.closedAt || null;
  if (c.closedBy !== undefined) r.closed_by = c.closedBy || null;
  if (c.reopenedAt !== undefined) r.reopened_at = c.reopenedAt || null;
  if (c.reopenedBy !== undefined) r.reopened_by = c.reopenedBy || null;
  if (c.modifiedAt !== undefined) r.modified_at = c.modifiedAt || null;
  if (c.modifiedBy !== undefined) r.modified_by = c.modifiedBy || null;
  if (c.audit !== undefined) r.audit = c.audit || [];
  if (c.notes !== undefined) r.notes = c.notes || null;
  return r;
}

// REGRA LINEAR (15/07/2026): falha → lança (nunca vazio); pagina até o fim.
// A janela de 90 dias tinha 4.704 comandas e o teto silencioso de 1000 fazia
// 3/4 delas nem chegarem ao app.
export async function listComandas({ unitId, caixaId, startDate, endDate, status } = {}) {
  const parts = ['select=*', 'order=date.desc,number.desc'];
  if (unitId) parts.push(`unit_id=eq.${encodeURIComponent(unitId)}`);
  if (caixaId) parts.push(`caixa_id=eq.${caixaId}`);
  if (startDate) parts.push(`date=gte.${startDate}`);
  if (endDate) parts.push(`date=lte.${endDate}`);
  if (status) parts.push(`status=eq.${encodeURIComponent(status)}`);
  const rows = await restFetchAll(`${SUPABASE_URL}/rest/v1/comandas?${parts.join('&')}`);
  return rows.map(comandaToApp);
}

export async function upsertComanda(comanda) {
  const row = { ...comandaToDb(comanda), updated_at: new Date().toISOString() };
  const isUpdate = comanda.id && comanda.id.length === 36 && comanda.id.includes('-');
  if (isUpdate) {
    // Guard atômico contra double-finalize (M-03): ao FINALIZAR, o PATCH só
    // aplica se a comanda ainda NÃO estiver finalizada. Se dois cliques/duas
    // recepções finalizam ao mesmo tempo, só o primeiro tem efeito — o
    // segundo casa com 0 linhas e não dá dupla baixa.
    let filter = `id=eq.${comanda.id}`;
    if (comanda.status === 'finalizada') filter += '&status=neq.finalizada';
    const r = await fetch(`${SUPABASE_URL}/rest/v1/comandas?${filter}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao atualizar comanda: ${await r.text()}`);
    const rows = await r.json();
    if (!rows.length) {
      // 0 linhas = alguém já finalizou antes. Retorna o estado atual sem erro.
      const cur = await fetch(`${SUPABASE_URL}/rest/v1/comandas?id=eq.${comanda.id}&select=*`, { headers: restHeaders() });
      const curRows = cur.ok ? await cur.json() : [];
      return curRows.length ? comandaToApp(curRows[0]) : null;
    }
    return comandaToApp(rows[0]);
  }
  // ─── CREATE com retry anti-corrida de número (G-02) ─────────────────────
  // O índice único parcial `comandas_numero_por_unidade` barra dois inserts
  // NATIVOS com o mesmo (unit_id, number) — a corrida do generateComandaNumber
  // (dois POST calculam o mesmo max+1). Quando isso acontece, buscamos o número
  // real no banco, regeramos max+1 e tentamos de novo: a comanda perdedora não
  // é descartada, só recebe o próximo número. A recepcionista nunca perde nada.
  const MAX_TRIES = 6;
  for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/comandas`, {
      method: 'POST',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (r.ok) return comandaToApp((await r.json())[0]);
    const text = await r.text();
    const numberConflict =
      (r.status === 409 || /23505/.test(text)) &&
      /comandas_numero_por_unidade|number/i.test(text);
    // Sem unit_id não dá pra regenerar; qualquer outro erro é real → propaga.
    if (!numberConflict || !row.unit_id || attempt === MAX_TRIES - 1) {
      throw new Error(`Falha ao criar comanda: ${text}`);
    }
    // Recalcula o próximo número a partir do MAX real das nativas da unidade.
    const q = `unit_id=eq.${encodeURIComponent(row.unit_id)}&avec_id=is.null&number=not.is.null&order=number.desc&limit=1&select=number`;
    const mr = await fetch(`${SUPABASE_URL}/rest/v1/comandas?${q}`, { headers: restHeaders() });
    const mrows = mr.ok ? await mr.json() : [];
    const curMax = mrows.length ? (parseInt(mrows[0].number, 10) || 0) : 0;
    row.number = curMax + 1 + attempt; // +attempt: diverge sob rajada concorrente
  }
  throw new Error('Falha ao criar comanda: número em conflito após múltiplas tentativas');
}

// ═══════════════════════════════════════════════════════════════════════════
// CASH MOVEMENTS (sangria, suprimento, entradas/saídas manuais)
// ═══════════════════════════════════════════════════════════════════════════

// cash_movements virou a tabela unificada de entradas E saidas (inclui o que
// antes era accounts_payable). Por isso o mapper cobre tambem: titulo,
// fornecedor, vencimento, dataPagamento, formaPagamento, recorrencia,
// parcela_atual/total, bankAccountId, anexoUrl, dataCompetencia, centroCusto,
// contaBancaria. Ver migrations/unify-cash-movements.sql.
export function movToApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    caixaId: row.caixa_id || null,
    unitId: row.unit_id || '',
    date: row.date || '',
    type: row.type || 'entrada',
    category: row.category || '',
    amount: num(row.amount),
    method: row.method || '',
    description: row.description || '',
    relatedComandaId: row.related_comanda_id || null,
    relatedProfId: row.related_prof_id || '',
    createdBy: row.created_by || '',
    // ─── Campos herdados de accounts_payable (saidas com vencimento) ────────
    titulo: row.titulo || '',
    fornecedor: row.fornecedor || '',
    // vencimento: a UI ja usa `dataVencimento` em alguns lugares — exponho os
    // dois pra evitar trocas em cascata
    vencimento: row.vencimento || '',
    dataVencimento: row.vencimento || '',
    dataPagamento: row.data_pagamento || '',
    formaPagamento: row.forma_pagamento || '',
    recorrencia: row.recorrencia || 'none',
    parcelaAtual: row.parcela_atual != null ? Number(row.parcela_atual) : null,
    parcelaTotal: row.parcela_total != null ? Number(row.parcela_total) : null,
    bankAccountId: row.bank_account_id || null,
    anexoUrl: row.anexo_url || '',
    dataCompetencia: row.data_competencia || '',
    competenciaManual: !!row.competencia_manual,
    centroCusto: row.centro_custo || '',
    contaBancaria: row.conta_bancaria || '',
    // Status: lê da coluna se existir, senão infere de data_pagamento.
    // Movements migrados de accounts_payable podem não ter status setado.
    status: row.status || (row.data_pagamento ? 'pago' : 'pendente'),
    notaFiscal: row.nota_fiscal || '',
    observacao: row.observacao || '',
    multaJuros: row.multa_juros != null ? Number(row.multa_juros) : 0,
    descontoTaxa: row.desconto_taxa != null ? Number(row.desconto_taxa) : 0,
    valorPago: row.valor_pago != null ? Number(row.valor_pago) : 0,
    // ─── Título (financeiro-002) — LEITURA. Escrita só pelas chaves próprias
    // novoEstado / motivoCancelamento (ver movToDb e utils/finTitulos.js).
    estado: row.estado || '',
    canceladoMotivo: row.cancelado_motivo || '',
    canceladoPor: row.cancelado_por || '',
    canceladoEm: row.cancelado_em || null,
    custoDe: row.custo_de || '',
    origem: row.origem || '',
    // chave do título — só LEITURA (rateio entre unidades, 08/10/2026); movToDb não a grava
    origemKey: row.origem_key || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Helper: converte string vazia / 'null' / undefined → null pra colunas
// DATE/TIMESTAMP. Postgres rejeita "" com erro 22007 (invalid input syntax).
// Aceita YYYY-MM-DD e ISO timestamps; qualquer outra coisa vai como null
// (deixa o Postgres decidir).
const dateOrNull = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  if (s === '' || s === 'null' || s === 'undefined') return null;
  return s;
};

export function movToDb(m) {
  const r = {};
  if (m.caixaId !== undefined) r.caixa_id = asUuidOrNull(m.caixaId);
  if (m.unitId !== undefined) r.unit_id = m.unitId || null;
  if (m.date !== undefined) r.date = dateOrNull(m.date);
  if (m.type !== undefined) r.type = m.type;
  if (m.category !== undefined) r.category = m.category || null;
  if (m.amount !== undefined) r.amount = num(m.amount);
  if (m.method !== undefined) r.method = m.method || null;
  if (m.description !== undefined) r.description = m.description || null;
  if (m.relatedComandaId !== undefined) r.related_comanda_id = asUuidOrNull(m.relatedComandaId);
  if (m.relatedProfId !== undefined) r.related_prof_id = m.relatedProfId || null;
  if (m.createdBy !== undefined) r.created_by = m.createdBy || null;
  // ─── Campos herdados de accounts_payable ──────────────────────────────────
  if (m.titulo !== undefined) r.titulo = m.titulo || null;
  if (m.fornecedor !== undefined) r.fornecedor = m.fornecedor || null;
  // Aceita os dois nomes (vencimento/dataVencimento). dataVencimento ganha
  // se ambos forem passados (vem direto dos forms da UI).
  if (m.dataVencimento !== undefined) r.vencimento = dateOrNull(m.dataVencimento);
  else if (m.vencimento !== undefined) r.vencimento = dateOrNull(m.vencimento);
  if (m.dataPagamento !== undefined) r.data_pagamento = dateOrNull(m.dataPagamento);
  if (m.formaPagamento !== undefined) r.forma_pagamento = m.formaPagamento || null;
  if (m.recorrencia !== undefined) r.recorrencia = m.recorrencia || null;
  if (m.parcelaAtual !== undefined) r.parcela_atual = m.parcelaAtual == null ? null : Number(m.parcelaAtual);
  if (m.parcelaTotal !== undefined) r.parcela_total = m.parcelaTotal == null ? null : Number(m.parcelaTotal);
  if (m.bankAccountId !== undefined) r.bank_account_id = asUuidOrNull(m.bankAccountId);
  if (m.anexoUrl !== undefined) r.anexo_url = m.anexoUrl || null;
  if (m.dataCompetencia !== undefined) r.data_competencia = dateOrNull(m.dataCompetencia);
  // competencia_manual (financeiro-002): só vai quando a tela manda (Contas a
  // Pagar, quando o usuário mexeu na competência) — nunca no caixa.
  if (m.competenciaManual !== undefined) r.competencia_manual = !!m.competenciaManual;
  if (m.centroCusto !== undefined) r.centro_custo = m.centroCusto || null;
  if (m.contaBancaria !== undefined) r.conta_bancaria = m.contaBancaria || null;
  // ─── Status + campos extras (NF, observação, pagamento) ─────────────────
  // O status só persiste se a coluna existir. Em paralelo, gravamos
  // data_pagamento quando pago — assim o status volta certo via fallback
  // mesmo em DBs sem a coluna `status`.
  if (m.status !== undefined) r.status = m.status || null;
  if (m.notaFiscal !== undefined) r.nota_fiscal = m.notaFiscal || null;
  if (m.observacao !== undefined) r.observacao = m.observacao || null;
  if (m.multaJuros !== undefined) r.multa_juros = m.multaJuros == null ? null : Number(m.multaJuros);
  if (m.descontoTaxa !== undefined) r.desconto_taxa = m.descontoTaxa == null ? null : Number(m.descontoTaxa);
  if (m.valorPago !== undefined) r.valor_pago = m.valorPago == null ? null : Number(m.valorPago);
  // ─── Título (07/10/2026) ───────────────────────────────────────────────
  // Cancelar/reabrir: chaves de ESCRITA próprias. `estado`/`canceladoMotivo`
  // (leitura) NÃO vão para o banco — várias telas salvam {...m, …} e o estado
  // velho iria junto. O gatilho fin_cm_antes exige o motivo e grava quem/quando.
  if (m.novoEstado !== undefined && m.novoEstado) r.estado = m.novoEstado;
  if (m.motivoCancelamento !== undefined) r.cancelado_motivo = m.motivoCancelamento || null;
  // "Unidade que usa o custo": vazio nunca é gravado (custo_de vazio mandaria
  // o título para o núcleo na DRE); sem valor, o banco usa a unidade que paga.
  if (m.custoDe !== undefined && m.custoDe) r.custo_de = m.custoDe;
  return r;
}

// REGRA LINEAR (15/07/2026): falha → lança; pagina até o fim (contas a pagar
// além de 1000 registros na janela sumiam da tela pelo teto silencioso).
// somenteCaixa (06/10/2026): só movimentos ligados a um caixa (sangria/
// suprimento) — é o único recorte que a recepção/gerente lê pelo /api/db
// (com unitId obrigatório); o resto de cash_movements é do operador financeiro.
export async function listCashMovements({ caixaId, unitId, startDate, endDate, type, somenteCaixa = false } = {}) {
  const parts = ['select=*', 'order=created_at.desc'];
  if (caixaId) parts.push(`caixa_id=eq.${caixaId}`);
  else if (somenteCaixa) parts.push('caixa_id=not.is.null');
  if (unitId) parts.push(`unit_id=eq.${encodeURIComponent(unitId)}`);
  if (startDate) parts.push(`date=gte.${startDate}`);
  if (endDate) parts.push(`date=lte.${endDate}`);
  if (type) parts.push(`type=eq.${encodeURIComponent(type)}`);
  const rows = await restFetchAll(`${SUPABASE_URL}/rest/v1/cash_movements?${parts.join('&')}`);
  return rows.map(movToApp);
}

// 09/10/2026 — "Oscar Freire + Jardins juntas" no Financeiro é uma VISÃO
// ('grupo-tree'): o id do grupo nunca vira unit_id (o lançamento fica numa
// unidade — a tela manda a principal). Cinto e suspensório das telas.
export const ehIdDeGrupoFinanceiro = (id) => /^(grupo|group)-/.test(String(id ?? ''));
export const TEXTO_UNIDADE_DE_GRUPO = 'Escolha a unidade (Oscar Freire + Jardins juntas é só uma visão: o lançamento fica numa unidade).';

export async function upsertCashMovement(mov) {
  if (mov && ehIdDeGrupoFinanceiro(mov.unitId)) throw new Error(TEXTO_UNIDADE_DE_GRUPO);
  const row = movToDb(mov);
  const isUpdate = mov.id && mov.id.length === 36 && mov.id.includes('-');
  if (isUpdate) {
    row.updated_at = new Date().toISOString();
    const r = await fetch(`${SUPABASE_URL}/rest/v1/cash_movements?id=eq.${mov.id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao atualizar movimento: ${await r.text()}`);
    return movToApp((await r.json())[0]);
  }
  const r = await fetch(`${SUPABASE_URL}/rest/v1/cash_movements`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(row),
  });
  if (!r.ok) throw new Error(`Falha ao criar movimento: ${await r.text()}`);
  return movToApp((await r.json())[0]);
}

// 22/10/2026 (lapidação do financeiro, etapa B): deleteCashMovement saiu.
// Lançamento não se apaga (o /api/db responde 405 e o banco recusa apagar
// conta a pagar, financeiro-019): para desfazer, cancele com o motivo.
// Nenhuma tela usava a função.
